import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { AudioFormat } from '../../../api/cc-smarts/getAudio';
import { AudioFeatureType } from '../../../api/cc-smarts/models/AudioFeature';
import type { AudioFeature } from '../../../types/audioFeature';
import { AudioPlayerStreamingProvider } from '../../context/AudioPlayerStreamingProvider';
import { useAudioPlaybackSync } from '../../hooks/useAudioPlaybackSync';
import { useAudioPlayerStreaming } from '../../hooks/useAudioPlayerStreaming';
import { useHoverWithDelayedExit } from '../../hooks/useHoverWithDelayedExit';

import { ExpandedAudioPlayer } from './ExpandedAudioPlayer';
import { MinifiedAudioPlayer } from './MinifiedAudioPlayer';

const styles = cssMap({
	boxShadowWrapper: {
		boxShadow: token('elevation.shadow.overflow'),
		borderRadius: token('radius.xlarge'),

		// TODO: All the designs here are temporary and are meant to be deleted
		// their only purpose is to test the audio player UI in Confluence for the time being
		// START
		position: 'fixed',
		top: token('space.1000'),
		right: token('space.400'),
		zIndex: 510,
		// END
	},
	container: {
		borderRadius: token('radius.xlarge'),
		position: 'relative',
		display: 'inline-block',
		verticalAlign: 'top',
		width: 'fit-content',
		overflow: 'hidden',
	},
});

type AudioContainerProps = {
	product: string;
	cloudId: string;
	activationId: string;
	audioFeatures: AudioFeature[];
};

export const AudioContainer = ({
	product,
	cloudId,
	activationId,
	audioFeatures,
}: AudioContainerProps) => {
	const { isHovered, handleMouseEnter, handleMouseLeave } = useHoverWithDelayedExit(3000, true);

	const { audioElementRef, fetchAudio } = useAudioPlayerStreaming({
		locale: 'en-US',
		type: AudioFeatureType.NARRATION,
		format: AudioFormat.MP3,
		product,
		cloudId,
		activationId,
	});

	useAudioPlaybackSync({ audioRef: audioElementRef });

	return (
		<AudioPlayerStreamingProvider fetchAudio={fetchAudio}>
			<Box xcss={styles.boxShadowWrapper}>
				<Box
					xcss={styles.container}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					{isHovered ? (
						<ExpandedAudioPlayer audioRef={audioElementRef} audioFeatures={audioFeatures} />
					) : (
						<MinifiedAudioPlayer
							audioRef={audioElementRef}
							title="Customer Service Roundup"
							byline="Scott Farquhar"
							avatarUrl="https://pbs.twimg.com/profile_images/803832195970433027/aaoG6PJI_400x400.jpg"
						/>
					)}
					<audio ref={audioElementRef} />
				</Box>
			</Box>
		</AudioPlayerStreamingProvider>
	);
};
